"use client";

import React from "react";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";
import { isTransparentNavbarRoute } from "~/utils/routes.utils";

import { STICKY_NAVBAR_CLASSES } from "./navbar.consts";
import { useNavbarOpacity } from "./useNavbarOpacity";

const DefaultNavbar = dynamic(() =>
  import("./DefaultNavbar").then((mod) => mod.DefaultNavbar),
);

type ScrollAwareNavbarProps = {
  /** Renders the logo-only nav, for the reduced shell minimal-shell routes use. */
  showLogoOnly?: boolean;
};

/**
 * Hosts the navbar. An opting-in route gets a sticky bar that starts transparent over
 * its hero and turns black once its trigger slides under. The wrapper is always there
 * so hydration cannot change the tree's shape and remount the navbar.
 */
const ScrollAwareNavbar = ({ showLogoOnly }: ScrollAwareNavbarProps) => {
  const pathname = usePathname();
  const isTransparentRoute = isTransparentNavbarRoute(pathname);

  const { isTransparent } = useNavbarOpacity();

  return (
    <div
      className={cn(isTransparentRoute && STICKY_NAVBAR_CLASSES)}
      // `data-navbar-transparent` is what the rules in `globals.css` key off. Absent
      // entirely on routes that never go transparent, so their navbar is untouched.
      data-navbar-transparent={isTransparentRoute ? isTransparent : undefined}
      data-testing-id={
        isTransparentRoute ? "scroll-aware-navbar" : "default-navbar-wrapper"
      }
    >
      <DefaultNavbar showLogoOnly={showLogoOnly} />
    </div>
  );
};

export { ScrollAwareNavbar };
